import { PublicClient } from 'viem'

import { ADDRESS_ZERO, MAKER_ERC20_ADDRESS } from '~/constants/addresses'
import { getChain, SupportedChainId } from '~/constants/chains'
import { IAddress, TokenContract } from '~/types'
import { EvmAddress } from '~/utils/address'
import { getReadErc20Contract } from '~/utils/erc20'
import { getReadErc721Contract } from '~/utils/erc721'
import { unwrapToken } from '~/utils/tokens'

/**
 * Get the symbol and decimals for multiple tokens in a single call.
 * This should be the preferred way to get the metadata for multiple tokens,
 * so if we need to change the way we get the metadata, we should do it here.
 *
 * @param tokens - The tokens to get the metadata for.
 * @param chainId - The chain ID to get the metadata for.
 * @param options - The options for the request. It only includes the public client.
 * @returns The symbol and decimals for the tokens.
 */
export const getMultipleTokenData = async (
  inputTokens: Array<{ address: IAddress; tokenId?: string }>,
  chainId: SupportedChainId,
  options: { publicClient: PublicClient },
): Promise<Array<TokenContract | undefined>> => {
  const { publicClient } = options

  // Normalise 0xEeee… warehouse sentinel to ADDRESS_ZERO so native-token branches match.
  const tokens = inputTokens.map((token) => ({
    ...token,
    address: unwrapToken(token.address),
  }))

  // Separate tokens by type
  const erc20Tokens = tokens.filter(
    (token) => !token.tokenId || token.address === ADDRESS_ZERO,
  )
  const nftTokens = tokens.filter(
    (token) => token.tokenId && token.address !== ADDRESS_ZERO,
  ) // ERC721 and ERC1155

  const results: Array<TokenContract | undefined> = new Array(tokens.length)

  // NFT and ERC20 lookups fill separate result slots, so they run together.
  // Handle NFT tokens (ERC721/ERC1155) with multicall for symbol()
  const loadNftTokens = async () => {
    if (nftTokens.length === 0) return
    const nftSymbolContracts = nftTokens.map(({ address }) => ({
      address: EvmAddress.format(address),
      abi: [
        {
          constant: true,
          inputs: [],
          name: 'symbol',
          outputs: [{ name: '', type: 'string' }],
          payable: false,
          stateMutability: 'view',
          type: 'function',
        },
      ] as const,
      functionName: 'symbol' as const,
      args: [] as const,
    }))

    try {
      const symbolResults = await publicClient.multicall({
        contracts: nftSymbolContracts,
      })

      nftTokens.forEach(({ address, tokenId }, i) => {
        const index = tokens.findIndex(
          (token) => token.address === address && token.tokenId === tokenId,
        )
        if (index !== -1) {
          const symbolResult = symbolResults[i]
          if (symbolResult.status === 'success') {
            results[index] = {
              symbol: symbolResult.result as string,
              decimals: 0,
            }
          } else {
            results[index] = undefined
          }
        }
      })
    } catch {
      // Fallback to individual calls if multicall fails
      await Promise.all(
        nftTokens.map(async ({ address, tokenId }) => {
          const index = tokens.findIndex(
            (token) => token.address === address && token.tokenId === tokenId,
          )
          if (index !== -1) {
            try {
              // Try ERC721 first
              const erc721Contract = getReadErc721Contract({
                address: EvmAddress.format(address),
                publicClient,
              })
              const symbol = await erc721Contract.read.symbol()
              results[index] = { symbol, decimals: 0 }
            } catch {
              // Getting the name for ERC1155 requires calling a 3rd party service (defined by the contract metadata).
              // We weren't previously doing this, and we're not doing it here for 3 reasons:
              // 1. It would slow down what's currently working.
              // 2. We will very likely replace this whole function with a server call.
              // 3. If implemented incorrectly, names could be wrong.
              results[index] = undefined
            }
          }
        }),
      )
    }
  }

  // Handle ERC20 tokens with multicall
  const loadErc20Tokens = async () => {
    if (erc20Tokens.length === 0) return
    const contracts = erc20Tokens.map(({ address }) => {
      if (address === ADDRESS_ZERO) {
        return {
          address: ADDRESS_ZERO,
          abi: [] as const,
          functionName: 'symbol' as const,
          args: [] as const,
        }
      }

      return {
        address: EvmAddress.format(address),
        abi: [
          {
            constant: true,
            inputs: [],
            name: 'symbol',
            outputs: [{ name: '', type: 'string' }],
            payable: false,
            stateMutability: 'view',
            type: 'function',
          },
        ] as const,
        functionName: 'symbol' as const,
        args: [] as const,
      }
    })

    const decimalsContracts = erc20Tokens.map(({ address }) => {
      if (address === ADDRESS_ZERO) {
        return {
          address: ADDRESS_ZERO,
          abi: [] as const,
          functionName: 'decimals' as const,
          args: [] as const,
        }
      }

      return {
        address: EvmAddress.format(address),
        abi: [
          {
            constant: true,
            inputs: [],
            name: 'decimals',
            outputs: [{ name: '', type: 'uint8' }],
            payable: false,
            stateMutability: 'view',
            type: 'function',
          },
        ] as const,
        functionName: 'decimals' as const,
        args: [] as const,
      }
    })

    try {
      const [symbolResults, decimalsResults] = await Promise.all([
        publicClient.multicall({ contracts }),
        publicClient.multicall({ contracts: decimalsContracts }),
      ])

      erc20Tokens.forEach(({ address, tokenId }, i) => {
        const index = tokens.findIndex(
          (token) => token.address === address && token.tokenId === tokenId,
        )
        if (index !== -1) {
          const symbolResult = symbolResults[i]
          const decimalsResult = decimalsResults[i]

          if (address === ADDRESS_ZERO) {
            const chainInfo = getChain(chainId)
            if (chainInfo) {
              results[index] = {
                symbol: chainInfo.nativeCurrency.symbol,
                decimals: chainInfo.nativeCurrency.decimals,
              }
            } else {
              // For unsupported chains, use placeholder values
              // The actual metadata will come from relay API
              results[index] = {
                symbol: 'UNKNOWN',
                decimals: 18,
              }
            }
          } else if (chainId === 1 && address === MAKER_ERC20_ADDRESS) {
            results[index] = { symbol: 'MKR', decimals: 18 }
          } else if (
            symbolResult.status === 'success' &&
            decimalsResult.status === 'success'
          ) {
            results[index] = {
              symbol: symbolResult.result as string,
              decimals: decimalsResult.result as number,
            }
          } else {
            results[index] = undefined
          }
        }
      })
    } catch {
      // Handle multicall failure by falling back to individual calls
      await Promise.all(
        erc20Tokens.map(async ({ address, tokenId }) => {
          const index = tokens.findIndex(
            (token) => token.address === address && token.tokenId === tokenId,
          )
          if (index !== -1) {
            if (address === ADDRESS_ZERO) {
              const chainInfo = getChain(chainId)
              if (chainInfo) {
                results[index] = {
                  symbol: chainInfo.nativeCurrency.symbol,
                  decimals: chainInfo.nativeCurrency.decimals,
                }
              } else {
                // For unsupported chains, use placeholder values
                // The actual metadata will come from relay API
                results[index] = {
                  symbol: 'UNKNOWN',
                  decimals: 18,
                }
              }
            } else if (chainId === 1 && address === MAKER_ERC20_ADDRESS) {
              results[index] = { symbol: 'MKR', decimals: 18 }
            } else {
              const erc20Contract = getReadErc20Contract({
                address,
                publicClient,
              })
              try {
                const [symbol, decimals] = await Promise.all([
                  erc20Contract.read.symbol(),
                  erc20Contract.read.decimals(),
                ])
                results[index] = { symbol, decimals }
              } catch {
                results[index] = undefined
              }
            }
          }
        }),
      )
    }
  }

  await Promise.all([loadNftTokens(), loadErc20Tokens()])

  return results
}
